import { useEffect, useRef, useState, useCallback } from "react";
import gsap from "gsap";
import {
  ServerIcon,
  CircleStackIcon,
  CpuChipIcon,
  ArrowPathIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import Spinner from "../../../shared/components/ui/Spinner";
import ClusterOverview from "./ClusterOverview";
import MachineCard from "./MachineCard";
import GPUPanel from "./GPUPanel";
import QueueManager from "./QueueManager";
import AlertBanner, { AlertHistory } from "./AlertBanner";
import {
  PIPELINE_SERVICES,
  SUPPORT_SERVICES,
  SERVICE_LABELS,
  SERVICE_DESCRIPTIONS,
  SERVICE_COLORS,
  QUEUE_MAP,
  REFRESH_INTERVALS,
  STATUS_COLORS,
} from "../constants";
import {
  fetchClusterOverview,
  fetchMachines,
  fetchMachine,
  fetchQueues,
  fetchDLQ,
  fetchAlerts,
  acknowledgeAlert,
  flushQueue,
  retryDLQJob,
  retryAllDLQ,
} from "../../../api";

/**
 * Service card component (legacy, kept for backward compatibility)
 */
function ServiceCard({ service, container, queueLen, hasDocker, index, cardRef }) {
  const isOnline = hasDocker
    ? container?.status === "running"
    : queueLen !== undefined;

  return (
    <div
      className="system-service-card"
      ref={cardRef}
      style={{ "--service-color": SERVICE_COLORS[service] }}
    >
      <div
        className={`service-status-indicator ${(() => {
          if (isOnline) return "online";
          if (container) return "offline";
          return "unknown";
        })()}`}
      />
      <div className="service-info">
        <div className="service-name">{SERVICE_LABELS[service]}</div>
        <div className="service-desc">{SERVICE_DESCRIPTIONS[service]}</div>
      </div>
      <div className="service-metrics">
        {queueLen !== undefined && (
          <div className="metric">
            <span className="metric-value">{queueLen ?? "-"}</span>
            <span className="metric-label">Queue</span>
          </div>
        )}
        {container?.uptime && (
          <div className="metric uptime">
            <span className="metric-value">{container.uptime}</span>
            <span className="metric-label">Uptime</span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Redesigned System Tab with multi-machine support, GPU monitoring,
 * queue management, and alerting.
 */
export default function SystemTab({ systemStatus }) {
  const serviceCardsRef = useRef([]);
  const resourceBarsRef = useRef([]);

  // New dashboard state
  const [clusterData, setClusterData] = useState(null);
  const [machines, setMachines] = useState([]);
  const [machineMetrics, setMachineMetrics] = useState({});
  const [queues, setQueues] = useState([]);
  const [dlqJobs, setDlqJobs] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMachine, setExpandedMachine] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      // Fetch all dashboard data in parallel
      const [clusterRes, machinesRes, queuesRes, dlqRes, alertsRes] = await Promise.all([
        fetchClusterOverview().catch(() => null),
        fetchMachines().catch(() => ({ machines: [] })),
        fetchQueues().catch(() => ({ queues: [] })),
        fetchDLQ().catch(() => ({ jobs: [] })),
        fetchAlerts('active', 50).catch(() => ({ alerts: [] })),
      ]);

      setClusterData(clusterRes);
      setMachines(machinesRes?.machines || []);
      setQueues(queuesRes?.queues || []);
      setDlqJobs(dlqRes?.jobs || []);
      setAlerts(alertsRes?.alerts || []);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load and refresh interval
  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, REFRESH_INTERVALS.cluster);
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  // Load detailed metrics for expanded machine
  useEffect(() => {
    if (!expandedMachine) return;

    const loadMachineMetrics = async () => {
      try {
        const res = await fetchMachine(expandedMachine);
        if (res?.metrics) {
          setMachineMetrics((prev) => ({
            ...prev,
            [expandedMachine]: res.metrics,
          }));
        }
      } catch (err) {
        console.error(`Failed to load metrics for ${expandedMachine}:`, err);
      }
    };

    loadMachineMetrics();
    const interval = setInterval(loadMachineMetrics, 5000);
    return () => clearInterval(interval);
  }, [expandedMachine]);

  // Animate service cards on mount
  useEffect(() => {
    if (serviceCardsRef.current.length > 0) {
      gsap.fromTo(
        serviceCardsRef.current.filter(Boolean),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.4, stagger: 0.05, ease: "power2.out" }
      );
    }
  }, []);

  // Animate resource bars when data updates
  useEffect(() => {
    if (systemStatus?.system && resourceBarsRef.current.length > 0) {
      const cpuPercent = systemStatus.system.cpu?.percent || 0;
      const memPercent = systemStatus.system.memory?.percent || 0;
      const diskPercent = systemStatus.system.disk?.percent || 0;

      resourceBarsRef.current.forEach((el, idx) => {
        if (!el) return;
        const percent = [cpuPercent, memPercent, diskPercent][idx] || 0;
        gsap.to(el, {
          width: `${percent}%`,
          duration: 0.8,
          ease: "power2.out",
        });
      });
    }
  }, [systemStatus]);

  // Handler functions
  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await acknowledgeAlert(alertId);
      setAlerts((prev) =>
        prev.map((a) =>
          a.id === alertId ? { ...a, acknowledged_at: new Date().toISOString() } : a
        )
      );
    } catch (err) {
      console.error("Failed to acknowledge alert:", err);
    }
  };

  const handleFlushQueue = async (queueName) => {
    try {
      await flushQueue(queueName);
      // Reload queues
      const res = await fetchQueues();
      setQueues(res?.queues || []);
    } catch (err) {
      console.error("Failed to flush queue:", err);
    }
  };

  const handleRetryDLQ = async (jobIndex) => {
    try {
      await retryDLQJob(jobIndex);
      // Reload DLQ
      const res = await fetchDLQ();
      setDlqJobs(res?.jobs || []);
    } catch (err) {
      console.error("Failed to retry DLQ job:", err);
    }
  };

  const handleRetryAllDLQ = async () => {
    try {
      await retryAllDLQ();
      // Reload DLQ
      const res = await fetchDLQ();
      setDlqJobs(res?.jobs || []);
    } catch (err) {
      console.error("Failed to retry all DLQ jobs:", err);
    }
  };

  // Get containers and queues from legacy systemStatus
  const containers = Array.isArray(systemStatus?.containers) ? systemStatus.containers : [];
  const hasDocker = containers.length > 0;
  const legacyQueues = systemStatus?.queues || {};

  const getQueueLen = (service) => {
    const queueName = QUEUE_MAP[service];
    if (!queueName) return undefined;
    const len = legacyQueues[queueName];
    return len !== undefined && len !== -1 ? len : undefined;
  };

  const getContainer = (service) => containers.find((c) => c.name === service);

  let cardIndex = 0;

  // Check if we have enhanced dashboard data
  const hasEnhancedData = clusterData || machines.length > 0 || queues.length > 0;

  // Show loading state only on initial load
  if (!systemStatus && loading) {
    return (
      <div className="stats-loading-section">
        <Spinner size="md" />
        Loading system status...
      </div>
    );
  }

  return (
    <div className="system-tab-v2">
      {/* Alert Banner */}
      <AlertBanner
        alerts={alerts}
        onAcknowledge={handleAcknowledgeAlert}
      />

      {/* Cluster Overview */}
      {hasEnhancedData && (
        <ClusterOverview
          data={clusterData}
          onRefresh={loadDashboardData}
        />
      )}

      {/* Machines Section (new multi-machine view) */}
      {machines.length > 0 && (
        <div className="stats-subsection">
          <div className="subsection-header">
            <h3>Machines</h3>
            <button className="btn-add-machine" title="Add Machine">
              <PlusIcon className="icon-sm" />
              Add Machine
            </button>
          </div>
          <div className="machines-grid">
            {machines.map((machine) => (
              <MachineCard
                key={machine.hostname}
                machine={machine}
                metrics={machineMetrics[machine.hostname]}
                expanded={expandedMachine === machine.hostname}
                onExpand={setExpandedMachine}
              />
            ))}
          </div>
        </div>
      )}

      {/* GPU Panel (if expanded machine has GPUs) */}
      {expandedMachine && machineMetrics[expandedMachine]?.gpus?.length > 0 && (
        <div className="stats-subsection">
          <GPUPanel
            gpus={machineMetrics[expandedMachine].gpus}
            hostname={expandedMachine}
          />
        </div>
      )}

      {/* Queue Management (new) */}
      {queues.length > 0 && (
        <div className="stats-subsection">
          <QueueManager
            queues={queues}
            dlqJobs={dlqJobs}
            onFlushQueue={handleFlushQueue}
            onRetryDLQ={handleRetryDLQ}
            onRetryAllDLQ={handleRetryAllDLQ}
          />
        </div>
      )}

      {/* Docker error message */}
      {systemStatus?.containers?.error && (
        <p className="stats-hint" style={{ marginBottom: 12, color: "var(--muted)" }}>
          Docker status unavailable. Queue counts shown below.
        </p>
      )}

      {/* Pipeline Services (legacy view) */}
      <div className="stats-subsection">
        <h3>Pipeline Services</h3>
        <div className="system-services-grid">
          {PIPELINE_SERVICES.map((service) => {
            const idx = cardIndex++;
            return (
              <ServiceCard
                key={service}
                service={service}
                container={getContainer(service)}
                queueLen={getQueueLen(service)}
                hasDocker={hasDocker}
                index={idx}
                cardRef={(el) => (serviceCardsRef.current[idx] = el)}
              />
            );
          })}
        </div>
      </div>

      {/* Support Services (legacy view) */}
      <div className="stats-subsection">
        <h3>Support Services</h3>
        <div className="system-services-grid">
          {SUPPORT_SERVICES.map((service) => {
            const idx = cardIndex++;
            return (
              <ServiceCard
                key={service}
                service={service}
                container={getContainer(service)}
                queueLen={getQueueLen(service)}
                hasDocker={hasDocker}
                index={idx}
                cardRef={(el) => (serviceCardsRef.current[idx] = el)}
              />
            );
          })}
        </div>
      </div>

      {/* Infrastructure Status */}
      <div className="stats-subsection">
        <h3>Infrastructure</h3>
        <div className="system-infra-grid">
          {/* Redis */}
          {(() => {
            const redis = containers.find((c) => c.name === "construct");
            const redisOnline = redis?.status === "running";
            const redisQueuesOk = systemStatus?.queues && !systemStatus.queues.error;
            const isOnline = hasDocker ? redisOnline : redisQueuesOk;
            const statusLabel = hasDocker
              ? redisOnline
                ? "Connected"
                : redis?.status || "Unknown"
              : redisQueuesOk
                ? "Connected"
                : "Unknown";
            return (
              <div className="infra-card">
                <div className="infra-icon">
                  <ServerIcon className="icon" />
                </div>
                <div className="infra-name">Redis (Construct)</div>
                <div className={`infra-status ${(() => {
                  if (isOnline) return "online";
                  if (redis) return "offline";
                  return "unknown";
                })()}`}>
                  {statusLabel}
                </div>
                {redis?.uptime && (
                  <div className="infra-uptime">{redis.uptime}</div>
                )}
              </div>
            );
          })()}

          {/* PostgreSQL */}
          {(() => {
            const db = containers.find((c) => c.name === "operator-db");
            const apiResponding = !!systemStatus;
            return (
              <div className="infra-card">
                <div className="infra-icon">
                  <CircleStackIcon className="icon" />
                </div>
                <div className="infra-name">PostgreSQL</div>
                <div className={`infra-status ${apiResponding ? "online" : "unknown"}`}>
                  {apiResponding ? "Connected" : "Unknown"}
                </div>
                {db?.uptime && (
                  <div className="infra-uptime">{db.uptime}</div>
                )}
              </div>
            );
          })()}

          {/* Deus (Ollama) */}
          {(() => {
            const deus = containers.find((c) => c.name === "deus");
            const deusOnline = deus?.status === "running";
            const statusLabel = hasDocker
              ? deusOnline
                ? "Running"
                : deus?.status || "Unknown"
              : "N/A";
            return (
              <div className="infra-card">
                <div className="infra-icon">
                  <CpuChipIcon className="icon" />
                </div>
                <div className="infra-name">Deus (Ollama)</div>
                <div className={`infra-status ${(() => {
                  if (deusOnline) return "online";
                  if (deus) return "offline";
                  return "unknown";
                })()}`}>
                  {statusLabel}
                </div>
                {deus?.uptime && (
                  <div className="infra-uptime">{deus.uptime}</div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* System Resources */}
      <div className="stats-subsection">
        <h3>System Resources</h3>
        {systemStatus?.system?.error ? (
          <p className="stats-hint">{systemStatus.system.error}</p>
        ) : (
          <div className="system-resources-grid">
            <div className="resource-card">
              <div className="resource-icon">CPU</div>
              <div className="resource-label">
                CPU ({systemStatus?.system?.cpu?.cores || "-"} cores)
              </div>
              <div className="resource-bar">
                <div
                  className="resource-fill"
                  ref={(el) => (resourceBarsRef.current[0] = el)}
                  style={{ width: "0%" }}
                />
              </div>
              <div className="resource-value">
                {systemStatus?.system?.cpu?.percent?.toFixed(1) || "-"}%
              </div>
            </div>
            <div className="resource-card">
              <div className="resource-icon">MEM</div>
              <div className="resource-label">Memory</div>
              <div className="resource-bar">
                <div
                  className="resource-fill"
                  ref={(el) => (resourceBarsRef.current[1] = el)}
                  style={{ width: "0%" }}
                />
              </div>
              <div className="resource-value">
                {systemStatus?.system?.memory?.used_gb?.toFixed(1) || "-"} /{" "}
                {systemStatus?.system?.memory?.total_gb?.toFixed(1) || "-"} GB
              </div>
            </div>
            <div className="resource-card">
              <div className="resource-icon">HDD</div>
              <div className="resource-label">Disk (/data)</div>
              <div className="resource-bar">
                <div
                  className="resource-fill"
                  ref={(el) => (resourceBarsRef.current[2] = el)}
                  style={{ width: "0%" }}
                />
              </div>
              <div className="resource-value">
                {systemStatus?.system?.disk?.used_gb?.toFixed(0) || "-"} /{" "}
                {systemStatus?.system?.disk?.total_gb?.toFixed(0) || "-"} GB
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Alert History */}
      {alerts.length > 0 && (
        <div className="stats-subsection">
          <AlertHistory
            alerts={alerts}
            onAcknowledge={handleAcknowledgeAlert}
          />
        </div>
      )}

      {/* Last refresh timestamp */}
      {lastRefresh && (
        <div className="stats-footer">
          <span className="last-refresh">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </span>
        </div>
      )}
    </div>
  );
}
